import { MapPin } from "lucide-react";

// Super-chat amount tiers -> colors (hex)
export const TIERS = [
  { min: 1000, color: "#EF4444", label: "$1000" },
  { min: 100, color: "#D946A6", label: "$100" },
  { min: 50, color: "#FF8A3D", label: "$50" },
  { min: 40, color: "#FFD93D", label: "$40" },
  { min: 20, color: "#22C55E", label: "$20" },
  { min: 10, color: "#22D3EE", label: "$10" },
  { min: 0, color: "#1E40AF", label: "$5" },
];

export const tierColor = (amount) => {
  const a = Number(amount) || 0;
  for (const t of TIERS) if (a >= t.min) return t.color;
  return "#1E40AF";
};

export const PRESET_AMOUNTS = [5, 10, 20, 40, 50, 100, 1000];
export const RADIUS_OPTIONS = [5, 10, 25, 50, 100, 200, 500, 1000]; // capped at 1000 miles
// Effectively unlimited: the max great-circle distance between any two points on
// Earth is ~12,450 miles, so this covers the entire planet with huge headroom.
export const UNLIMITED_RADIUS = 999999;

// Platform fee (your profit), matching the mobile backend formula:
// gifts under $100 -> $1 flat; $100+ -> 2.9% of the gift (min $1).
// NOTE: the backend DEDUCTS this from the gift (recipient receives gift - fee).
// This is display-only; the backend is the source of truth and returns platform_fee.
export const platformFee = (amount) => {
  const a = Number(amount) || 0;
  if (a <= 0) return 0;
  const fee = a < 100 ? 1 : Math.max(1, a * 0.029);
  return Math.round(fee * 100) / 100;
};

export const money = (n) => {
  const v = Number(n) || 0;
  return v % 1 === 0 ? `$${v}` : `$${v.toFixed(2)}`;
};

export const initials = (name = "") =>
  name.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";

// normalize a feed (live chat) item from the backend
const pickThanks = (it) => {
  const candidates = [it.thanks_text, it.thank_you, it.thanks, it.thanks_list, it.thank_you_note];
  for (const c of candidates) {
    if (!c) continue;
    if (typeof c === "string") return c;
    if (Array.isArray(c)) { const t = c[0]; return typeof t === "string" ? t : t?.text || ""; }
    if (typeof c === "object") return c.text || c.message || "";
  }
  return "";
};

export const normFeed = (it) => ({
  id: it.id || it.donation_id || it._id,
  donorId: it.donor_id || it.donor?.id || it.donor?.user_id || null,
  donorRecipientId: it.donor_recipient_id || it.donor?.recipient_id || null,
  anonymous: it.anonymous ?? it.is_anonymous ?? false,
  donorName: it.anonymous ? "Anonymous" : it.donor_name || it.name || it.donor?.name || "Someone",
  donorPicture: it.anonymous ? null : it.donor_picture || it.picture || it.donor?.picture,
  donorRecipientId: it.donor_recipient_id || it.donor?.recipient_id || null,
  recipientId: it.recipient_id || it.recipient?.id || it.recipient?.recipient_id,
  recipientName: it.recipient_name || it.recipient?.name || "a neighbor",
  recipientPicture: it.recipient_picture || it.recipient?.picture,
  amount: it.amount != null ? it.amount : it.amount_cents != null ? it.amount_cents / 100 : 0,
  message: typeof it.message === "string" ? it.message : "",
  hearts: it.hearts ?? it.heart_count ?? 0,
  hearted: it.hearted ?? it.i_hearted ?? false,
  thanks: pickThanks(it),
  distance: it.donor_distance_miles ?? it.distance_miles ?? null,
  isMineAsRecipient: it.is_recipient_me ?? it.received_by_me ?? false,
  isMineAsDonor: it.is_donor_me ?? it.sent_by_me ?? it.donated_by_me ?? it.is_mine ?? false,
  createdAt: it.created_at || it.createdAt,
  method: it.method || null,
});

// normalize a nearby recipient
export const normRecipient = (r) => ({
  id: r.id || r.recipient_id || r._id,
  name: r.name || r.user_name || "Neighbor",
  picture: r.picture,
  story: r.story || "",
  distance: r.distance_miles ?? r.distance ?? null,
  place: r.zipcode_place ?? r.place ?? r.city ?? null,
  payoutsEnabled: r.payouts_enabled ?? r.can_receive ?? null,
  totalReceived: r.total_received ?? (r.received_cents != null ? r.received_cents / 100 : 0),
  hasStory: r.has_story ?? !!r.active_story,
  goalAmount: r.goal_amount ?? null,
  payoutMethods: r.payout_methods ?? [],
  zelleQr: r.zelle_qr_url ?? null,
  venmoQr: r.venmo_qr_url ?? null,
  zelleHandle: r.zelle_handle ?? null,
  venmoHandle: r.venmo_handle ?? null,
  isMe: r.is_me ?? false,
});

// normalize a fundraiser
export const normFundraiser = (f) => {
  const goal = f.goal_amount ?? f.goal ?? 0;
  const raised = f.raised ?? f.raised_amount ?? f.current_amount ?? (f.raised_cents != null ? f.raised_cents / 100 : 0);
  return {
    id: f.id || f.fundraiser_id || f._id,
    title: f.title || "Fundraiser",
    story: f.story || "",
    goal,
    raised,
    pct: goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : 0,
    contributors: f.contributors ?? f.contributor_count ?? f.contributors_count ?? 0,
    endsAt: f.ends_at || f.expires_at || f.ends || null,
    recipientId: f.recipient_id || f.owner_recipient_id || f.owner?.recipient_id || f.recipient?.id || null,
    ownerName:
      f.owner_name || f.recipient_name || f.creator_name || f.user_name || f.name ||
      f.owner?.name || f.recipient?.name || f.creator?.name || "Neighbor",
    ownerPicture:
      f.owner_picture || f.recipient_picture || f.creator_picture || f.user_picture || f.picture ||
      f.owner?.picture || f.recipient?.picture || f.creator?.picture || null,
    isMine: f.is_mine ?? f.mine ?? false,
  };
};

export const countdown = (endsAt) => {
  if (!endsAt) return "";
  const end = new Date(endsAt).getTime();
  const diff = end - Date.now();
  if (diff <= 0) return "ended";
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  return `${d}d ${h}h left`;
};

export const MapIcon = MapPin;

// Decorative portrait avatars used on the sign-in + map screens
export const DECOR_AVATARS = [
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&w=200&q=70",
  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&w=200&q=70",
  "https://images.unsplash.com/photo-1662850886700-4ec19bd30d11?crop=entropy&cs=srgb&fm=jpg&w=200&q=70",
  "https://images.pexels.com/photos/38670596/pexels-photo-38670596.jpeg?auto=compress&cs=tinysrgb&w=200",
  "https://images.unsplash.com/photo-1580489944761-15a19d654956?crop=entropy&cs=srgb&fm=jpg&w=200&q=70",
];

export const clockTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch (e) {
    return "";
  }
};
